import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'accent' | 'ink' | 'destructive' | 'link' | 'glass'

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

interface ButtonVariantOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    children?: ReactNode
}

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-sm shadow-primary/25 hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
    outline: 'border border-input bg-card text-foreground hover:bg-muted',
    ghost: 'text-foreground hover:bg-muted',
    accent: 'bg-accent text-accent-foreground hover:bg-accent/85',
    ink: 'bg-ink text-ink-foreground hover:bg-ink/90',
    destructive: 'bg-destructive text-white hover:bg-destructive/90',
    link: 'h-auto px-0 text-primary underline-offset-4 hover:underline',
    glass: 'border border-white/15 bg-white/10 text-white hover:bg-white/15'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 gap-1.5 px-3.5 text-sm',
    md: 'h-11 gap-2 px-5 text-sm',
    lg: 'h-12 gap-2 px-6 text-base',
    icon: 'size-10',
    'icon-sm': 'size-8'
}

export const buttonVariants = ({ variant = 'primary', size = 'md', className }: ButtonVariantOptions = {}): string => {
    return cn(
        'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-full font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
        SIZES[size],
        VARIANTS[variant],
        variant === 'link' && 'px-0',
        className
    )
}

export const Button: FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    loading = false,
    className,
    children,
    disabled,
    type = 'button',
    ...props
}): ReactNode => {
    return (
        <button
            type={type}
            className={buttonVariants({ variant, size, className })}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...props}
        >
            {loading && <Loader2 className='size-4 animate-spin' aria-hidden='true' />}
            {children}
        </button>
    )
}
